import { apiClient } from './api'

export interface AdminCreateUserData {
  email: string
  first_name: string
  last_name: string
  role: 'merchant_admin' | 'merchant_user' | 'submerchant_admin' | 'submerchant_user'
  company_name?: string
  phone_number?: string
  country?: number
  merchant_id?: number
  submerchant_id?: number
  send_welcome_email?: boolean
  // Merchant creation fields
  create_merchant?: boolean
  merchant_name?: string
  merchant_email?: string
  merchant_description?: string
  merchant_website_url?: string
  merchant_currency_id?: number
  merchant_country_id?: number
  merchant_success_redirect_url?: string
  merchant_cancel_redirect_url?: string
  merchant_webhook_url?: string
  merchant_commission_rate?: number
  // Payout configuration fields
  merchant_payout_crypto_enabled?: boolean
  merchant_payout_wallet_address?: string
  merchant_payout_crypto_type_id?: number
}

export interface AdminCreateUserResponse {
  success: boolean
  message: string
  user: {
    id: number
    email: string
    first_name: string
    last_name: string
    role: string
    is_active: boolean
    is_verified: boolean
    created_at: string
  }
  temporary_password: string
  email_sent: boolean
  merchant?: {
    id: number
    company_name: string
    email: string
    status: string
    is_verified: boolean
    created_at: string
  }
}

export interface AdminUserListResponse {
  success: boolean
  message: string
  data: {
    results: Array<{
      id: number
      email: string
      full_name: string
      role: string
      role_display: string
      is_active: boolean
      is_verified: boolean
      company_name?: string
      phone_number?: string
      date_joined: string
      merchant?: {
        id: number
        company_name: string
        email: string
        status: string
      }
      submerchant?: {
        id: number
        name: string
        status: string
      }
      sale_shops_count: number
      created_at: string
      last_login_display: string
    }>
    count: number
    next?: string
    previous?: string
  }
}

export interface AdminUserDetailResponse {
  success: boolean
  message: string
  user: {
    id: number
    email: string
    first_name: string
    last_name: string
    role: string
    role_display: string
    is_active: boolean
    is_verified: boolean
    is_account_approved: boolean
    company_name?: string
    phone_number?: string
    address?: string
    environment: string
    currency: string
    language: string
    theme: string
    brand_color: string
    name: string
    is_kyc_verified: boolean
    kyc_verification_date?: string
    kyc_verification_level: string
    kyc_verification_notes?: string
    date_joined: string
    last_login?: string
    country?: {
      id: number
      name: string
      country_code: string
    }
    merchant?: {
      id: number
      company_name: string
      email: string
      status: string
      is_verified: boolean
      website_url?: string
      description?: string
      currency: string
      commission_rate: number
      payout_crypto_enabled: boolean
      payout_wallet_address?: string
      payout_crypto_type?: string
      webhook_url?: string
      success_redirect_url?: string
      cancel_redirect_url?: string
      created_at: string
      users_count: number
      submerchants_count: number
    }
    submerchant?: {
      id: number
      name: string
      email: string
      status: string
      is_active: boolean
      is_verified: boolean
      website_url?: string
      description?: string
      currency: string
      commission_rate: number
      payout_crypto_enabled: boolean
      payout_wallet_address?: string
      payout_crypto_type?: string
      webhook_url?: string
      success_redirect_url?: string
      cancel_redirect_url?: string
      created_at: string
      merchant: {
        id: number
        company_name: string
      }
      users_count: number
    }
    sale_shops_count: number
    transactions_count: number
    created_at: string
  }
}

export interface AdminUpdateUserData {
  first_name?: string
  last_name?: string
  role?: string
  company_name?: string
  phone_number?: string
  country?: number
  is_active?: boolean
  is_verified?: boolean
}

/**
 * Admin Service for managing users and merchants
 */
export const adminService = {
  /**
   * Create a new user (admin only)
   */
  async createUser(data: AdminCreateUserData): Promise<AdminCreateUserResponse> {
    return apiClient.post('/auth/admin/users/create/', data)
  },

  /**
   * Get list of all users (admin only)
   */
  async getUsers(params?: {
    search?: string
    role?: string
    is_active?: boolean
    is_verified?: boolean
    environment?: string
    page?: number
    page_size?: number
  }): Promise<AdminUserListResponse> {
    const queryParams = new URLSearchParams()
    
    if (params?.search) queryParams.append('search', params.search)
    if (params?.role) queryParams.append('role', params.role)
    if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString())
    if (params?.is_verified !== undefined) queryParams.append('is_verified', params.is_verified.toString())
    if (params?.environment) queryParams.append('environment', params.environment)
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString())

    const queryString = queryParams.toString()
    const url = `/auth/admin/users/${queryString ? `?${queryString}` : ''}`
    
    return apiClient.get(url)
  },

  /**
   * Search users (admin only)
   */
  async searchUsers(query: string): Promise<AdminUserListResponse> {
    return this.getUsers({ search: query })
  },

  /**
   * Get user details by ID (admin only)
   * Note: Backend doesn't have individual user endpoint, so we search for the user
   */
  async getUserById(userId: number): Promise<AdminUserDetailResponse> {
    try {
      const response: any = await apiClient.get(`/auth/admin/users/${userId}/`)
      
      if (!response) {
        throw new Error('No response received from server')
      }
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch user details')
      }
      
      return response as AdminUserDetailResponse
    } catch (error: any) {
      // Fallback to the old method if the new endpoint fails
      try {
        const listResponse = await this.getUsers({ page: 1, page_size: 1000 })
        
        if (listResponse.success && listResponse.data.results) {
          const user = listResponse.data.results.find((u: any) => u.id === userId)
          if (user) {
            // Convert list user format to detail user format
            const userDetail = {
              id: user.id,
              email: user.email,
              first_name: user.full_name?.split(' ')[0] || '',
              last_name: user.full_name?.split(' ').slice(1).join(' ') || '',
              role: user.role,
              role_display: user.role_display,
              date_joined: user.date_joined,
              is_active: user.is_active,
              is_verified: user.is_verified,
              is_account_approved: false, // Default value
              company_name: user.company_name,
              phone_number: user.phone_number,
              address: undefined, // Changed from null to undefined
              environment: 'sandbox', // Default value
              currency: 'USD', // Default value
              language: 'en', // Default value
              theme: 'dark', // Default value
              brand_color: '#FF6B35', // Default value
              name: 'PEXIBLOCK', // Default value
              is_kyc_verified: false, // Default value
              kyc_verification_date: undefined, // Changed from null to undefined
              kyc_verification_level: 'BASIC', // Default value
              kyc_verification_notes: undefined, // Changed from null to undefined
              country: (user as any).country || undefined,
              merchant: user.merchant ? {
                ...user.merchant,
                is_verified: false,
                website_url: undefined,
                description: undefined,
                currency: 'USD',
                commission_rate: 0,
                payout_crypto_enabled: false,
                payout_wallet_address: undefined,
                payout_crypto_type: undefined,
                webhook_url: undefined,
                success_redirect_url: undefined,
                cancel_redirect_url: undefined,
                created_at: user.created_at,
                users_count: 0,
                submerchants_count: 0
              } : undefined,
              submerchant: user.submerchant ? {
                ...user.submerchant,
                email: '',
                is_active: true,
                is_verified: false,
                website_url: undefined,
                description: undefined,
                currency: 'USD',
                commission_rate: 0,
                payout_crypto_enabled: false,
                payout_wallet_address: undefined,
                payout_crypto_type: undefined,
                webhook_url: undefined,
                success_redirect_url: undefined,
                cancel_redirect_url: undefined,
                created_at: user.created_at,
                merchant: { id: 0, company_name: '' },
                users_count: 0
              } : undefined,
              sale_shops_count: user.sale_shops_count || 0,
              transactions_count: 0, // Default value
              created_at: user.created_at,
              last_login: user.last_login_display
            }
            
            return {
              success: true,
              message: 'User retrieved successfully (fallback method)',
              user: userDetail
            }
          }
        }
        
        throw new Error('User not found')
      } catch (fallbackError: any) {
        throw new Error(this.handleError(error))
      }
    }
  },

  /**
   * Update user (admin only)
   */
  async updateUser(userId: number, data: AdminUpdateUserData): Promise<AdminUserDetailResponse> {
    return apiClient.patch(`/auth/admin/users/${userId}/`, data)
  },

  /**
   * Deactivate user (admin only)
   * Note: Backend doesn't have individual user deactivate endpoint
   */
  async deactivateUser(_userId: number): Promise<{ success: boolean; message: string }> {
    return {
      success: false,
      message: 'User deactivation functionality is not available. Backend endpoint not implemented.'
    }
  },

  /**
   * Activate user (admin only)
   * Note: Backend doesn't have individual user activate endpoint
   */
  async activateUser(_userId: number): Promise<{ success: boolean; message: string }> {
    return {
      success: false,
      message: 'User activation functionality is not available. Backend endpoint not implemented.'
    }
  },

  /**
   * Resend welcome email (admin only)
   * Note: Backend doesn't have individual user resend welcome endpoint
   */
  async resendWelcomeEmail(_userId: number): Promise<{ success: boolean; message: string }> {
    return {
      success: false,
      message: 'Resend welcome email functionality is not available. Backend endpoint not implemented.'
    }
  },

  /**
   * Error handling helper
   */
  handleError(error: any): string {
    if (error.response?.data?.message) {
      return error.response.data.message
    }
    if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
      return error.response.data.errors.join(', ')
    }
    if (error.response?.data?.errors && typeof error.response.data.errors === 'object') {
      return Object.values(error.response.data.errors).flat().join(', ')
    }
    return error.message || 'An unexpected error occurred'
  }
}
